/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useState, useEffect, useMemo } from "react";
import { Textarea, TextInput } from "flowbite-react";
import { HiOutlinePlus, HiOutlineTrash } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { Location } from "../../models";
import {
  AddressSearchInput,
  AddressSearchResult,
  Button,
  StandardForm,
  EntityFormDialog,
  InfoMessageAtom,
} from "@components";
import { useWorkspaceData } from "@data";
import { formFooter } from "@design-system";
import { IContactPerson } from "../../types";

export interface LocationModalProp {
  data: Location;
  isShowing: boolean;
  onConfirm: (data: Location) => void | boolean | Promise<void | boolean>;
  onCancel: () => void;
  showSaveToggle?: boolean;
  shouldSaveLocation?: boolean;
  onSaveToggleChange?: (shouldSave: boolean) => void;
}

export const LocationForm: FC<LocationModalProp> = function (
  props: LocationModalProp,
) {
  const { t } = useTranslation(["common", "location", "modals"]);

  const [isNew] = useState(Location.isNew(props.data));

  // State for address fields to enable autocomplete
  const [addressLine, setAddressLine] = useState(props.data.addressLine ?? "");
  const [postCode, setPostCode] = useState(props.data.postCode ?? "");
  const [city, setCity] = useState(props.data.city ?? "");
  const [displayName, setDisplayName] = useState(props.data.displayName ?? "");
  const [description, setDescription] = useState(props.data.description ?? "");
  const [contacts, setContacts] = useState<IContactPerson[]>(
    props.data.contacts ?? [],
  );
  const [contactsValidationFailed, setContactsValidationFailed] =
    useState(false);
  const [shouldSave, setShouldSave] = useState(
    props.shouldSaveLocation ?? true,
  );
  const [isAddressValid, setIsAddressValid] = useState(!isNew); // Existing locations are valid by default
  const [showAddressValidationError, setShowAddressValidationError] =
    useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);

  // Eksisterende lokasjoner (for duplicate detection) fra det sentrale datalageret.
  const { locations: existingLocations } = useWorkspaceData();

  // Update local state when props.data changes
  useEffect(() => {
    setAddressLine(props.data.addressLine ?? "");
    setPostCode(props.data.postCode ?? "");
    setCity(props.data.city ?? "");
    setDisplayName(props.data.displayName ?? "");
    setDescription(props.data.description ?? "");
    setContacts(props.data.contacts ?? []);
    setContactsValidationFailed(false);
    setShowAddressValidationError(false);
    setSubmissionFailed(false);
    setIsAddressValid(!Location.isNew(props.data));
  }, [props.data]);

  // Update shouldSave when prop changes
  useEffect(() => {
    setShouldSave(props.shouldSaveLocation ?? true);
  }, [props.shouldSaveLocation]);

  // Check for duplicate location
  const duplicateLocation = useMemo(() => {
    if (!addressLine.trim() || !postCode.trim() || !city.trim()) {
      return null;
    }

    // Don't check for duplicates if we're editing the same location
    if (!isNew && props.data.id) {
      return existingLocations.find(
        (loc) =>
          loc.id !== props.data.id &&
          loc.addressLine?.toLowerCase().trim() ===
            addressLine.toLowerCase().trim() &&
          loc.postCode?.toLowerCase().trim() ===
            postCode.toLowerCase().trim() &&
          loc.city?.toLowerCase().trim() === city.toLowerCase().trim(),
      );
    }

    // For new locations, check all existing locations
    return existingLocations.find(
      (loc) =>
        loc.addressLine?.toLowerCase().trim() ===
          addressLine.toLowerCase().trim() &&
        loc.postCode?.toLowerCase().trim() === postCode.toLowerCase().trim() &&
        loc.city?.toLowerCase().trim() === city.toLowerCase().trim(),
    );
  }, [addressLine, postCode, city, existingLocations, isNew, props.data.id]);

  const handleAddressSelect = (address: AddressSearchResult) => {
    setAddressLine(address.addressLine);
    setPostCode(address.postCode);
    setCity(address.city);
    // Auto-fill display name with address line if display name is empty
    if (!displayName.trim()) {
      setDisplayName(address.addressLine);
    }
  };

  const handleAddressValidated = (isValid: boolean) => {
    setIsAddressValid(isValid);
    if (isValid) setShowAddressValidationError(false);
  };

  const handleConfirm = async (data: Location) => {
    const hasCompleteAddress =
      addressLine.trim().length >= 5 &&
      postCode.trim().length >= 2 &&
      city.trim().length >= 2;

    // Autocomplete is optional, but the resulting address must be complete.
    if (!isAddressValid && !hasCompleteAddress) {
      setShowAddressValidationError(true);
      return;
    }

    const contactsAreValid = contacts.every(
      (contact) =>
        contact.name.trim().length >= 3 &&
        /^\+?[0-9][0-9 ()./-]{2,49}$/.test(contact.phoneNumber.trim()),
    );
    if (!contactsAreValid) {
      setContactsValidationFailed(true);
      return;
    }

    // Merge form data with our local state to ensure addressLine is included
    const mergedData = {
      ...data,
      addressLine,
      postCode,
      city,
      displayName,
      description,
      contacts: contacts.map((contact) => ({
        name: contact.name.trim(),
        phoneNumber: contact.phoneNumber.trim(),
      })),
    };
    setSubmissionFailed(false);
    const result = await props.onConfirm(mergedData as Location);
    if (result === false) setSubmissionFailed(true);
  };

  const addContact = () => {
    setContacts((current) => [
      ...current,
      { name: "", phoneNumber: "" },
    ]);
  };

  const updateContact = (
    index: number,
    field: "name" | "phoneNumber",
    value: string,
  ) => {
    setContacts((current) =>
      current.map((contact, contactIndex) =>
        contactIndex === index ? { ...contact, [field]: value } : contact,
      ),
    );
  };

  const removeContact = (index: number) => {
    setContacts((current) =>
      current.filter((_, contactIndex) => contactIndex !== index),
    );
  };

  return (
    <>
      <StandardForm
        showHeader={false}
        description={t("modals:descriptions.new_location")}
        fields={[
          {
            id: "addressLine",
            label: t("common:address_line"),
            grid_style: "col-span-2",
            input: (
              <div className="space-y-2 w-full">
                <AddressSearchInput
                  id="location-address-search"
                  name="location-address-search"
                  placeholder={t("common:placeholder.address_line")}
                  value={addressLine}
                  onChange={setAddressLine}
                  onSelectAddress={handleAddressSelect}
                  onAddressValidated={handleAddressValidated}
                  required={true}
                  showValidationError={
                    showAddressValidationError &&
                    (addressLine.trim().length < 5 ||
                      postCode.trim().length < 2 ||
                      city.trim().length < 2)
                  }
                />
                {duplicateLocation && (
                  <div className="w-full max-w-full break-words">
                    <InfoMessageAtom
                      variant="warning"
                      title={t("location:duplicate_warning_title", {
                        defaultValue: "Location already exists",
                      })}
                    >
                      {t("location:duplicate_warning_message", {
                        defaultValue:
                          "{{name}} has the same address. We recommend re-using the same location",
                        name: duplicateLocation.displayName,
                      })}
                    </InfoMessageAtom>
                  </div>
                )}
              </div>
            ),
          },
          {
            id: "postCode",
            label: t("common:postcode"),
            input: (
              <TextInput
                id="postCode"
                name="postCode"
                placeholder={t("common:placeholder.postcode")}
                value={postCode}
                onChange={(e) => setPostCode(e.target.value)}
                autoComplete="new-password"
              />
            ),
          },
          {
            id: "city",
            label: t("common:city"),
            input: (
              <TextInput
                id="city"
                name="city"
                placeholder={t("common:placeholder.city")}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                autoComplete="new-password"
              />
            ),
          },
          {
            id: "displayName",
            label: t("location:modal.display_name"),
            grid_style: "col-span-2",
            input: (
              <TextInput
                id="displayName"
                name="displayName"
                placeholder={t("location:modal.display_name")}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                type="text"
                required={true}
                autoComplete="new-password"
              />
            ),
          },
          {
            id: "description",
            label: t("common:placeholder.description"),
            grid_style: "col-span-2",
            input: (
              <Textarea
                id="description"
                key="description"
                name="description"
                className="text-sm"
                placeholder={t("common:placeholder.description")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            ),
          },
          {
            id: "contacts",
            label: t("location:contacts.title"),
            grid_style: "col-span-2",
            input: (
              <div className="w-full space-y-3">
                {contacts.map((contact, index) => {
                  const nameInvalid =
                    contactsValidationFailed && contact.name.trim().length < 3;
                  const phoneInvalid =
                    contactsValidationFailed &&
                    !/^\+?[0-9][0-9 ()./-]{2,49}$/.test(
                      contact.phoneNumber.trim(),
                    );
                  return (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]"
                    >
                      <TextInput
                        aria-label={t("location:contacts.name")}
                        placeholder={t("location:contacts.name")}
                        value={contact.name}
                        color={nameInvalid ? "failure" : undefined}
                        onChange={(event) =>
                          updateContact(index, "name", event.target.value)
                        }
                      />
                      <TextInput
                        aria-label={t("location:contacts.phone")}
                        placeholder={t("location:contacts.phone")}
                        value={contact.phoneNumber}
                        color={phoneInvalid ? "failure" : undefined}
                        onChange={(event) =>
                          updateContact(
                            index,
                            "phoneNumber",
                            event.target.value,
                          )
                        }
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<HiOutlineTrash className="h-4 w-4" />}
                        iconPosition="only"
                        aria-label={t("location:contacts.remove")}
                        onClick={() => removeContact(index)}
                      />
                    </div>
                  );
                })}
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<HiOutlinePlus className="h-4 w-4" />}
                  onClick={addContact}
                >
                  {t("location:contacts.add")}
                </Button>
              </div>
            ),
          },
        ]}
        onConfirm={handleConfirm}
        onCancel={props.onCancel}
        buttonConfirmText={t("modals:button_texts.location")}
        buttonConfirmPrependIcon={
          isNew ? <HiOutlinePlus className="mr-2 h-4 w-4" /> : null
        }
        buttonCloseText={t("common:close")}
      />

      {submissionFailed && (
        <InfoMessageAtom variant="error" className="mt-4">
          {t("location:save_failed", {
            defaultValue:
              "Lokasjonen kunne ikke lagres. Prøv igjen, eller kontakt support hvis problemet fortsetter.",
          })}
        </InfoMessageAtom>
      )}

      {/* Legg til toggle-knappen hvis showSaveToggle er true */}
      {props.showSaveToggle && (
        <div className={`${formFooter} mt-4`}>
          <div className="flex items-center py-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={shouldSave}
                className="sr-only peer"
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setShouldSave(isChecked);

                  if (props.onSaveToggleChange) {
                    props.onSaveToggleChange(isChecked);
                  }
                }}
              />
              <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-gray-600 peer-checked:bg-gradient-to-r peer-checked:from-blue-400 peer-checked:to-lgb-blue-300"></div>
              <span className="ml-4 text-sm font-medium text-gray-900 dark:text-gray-300">
                {shouldSave
                  ? t("location:save_for_reuse")
                  : t("location:one_time_use")}
              </span>
            </label>
          </div>
        </div>
      )}
    </>
  );
};

export const LocationModal: FC<LocationModalProp> = function (
  props: LocationModalProp,
) {
  const { t } = useTranslation(["location"]);

  return (
    <EntityFormDialog
      title={
        Location.isNew(props.data)
          ? t("location:modal.create_location")
          : t("location:modal.update_location")
      }
      show={props.isShowing}
      onCancel={props.onCancel}
    >
      <LocationForm {...props} />
    </EntityFormDialog>
  );
};
